import React, {useCallback, useState} from 'react';

import StudentLogin from '../student-login/student-login.jsx';
import {getLabcodeApiBaseUrl, staffLogin} from '../../lib/labcode-api';
import {setLabcodeStaffSession} from '../../lib/labcode-staff-session';

import styles from './login-hub.css';
import labcodeIconLogoUrl from './labcode-icon-logo.svg';

const URL_CONTATO = 'https://www.labcriar.com.br';
const URL_PRIVACIDADE = 'https://www.labcriar.com.br/politica-de-privacidade';
const URL_TERMOS = 'https://www.labcriar.com.br';

const SCREENS = {
    LANDING: 'landing',
    STUDENT: 'student',
    STAFF_EDUCATOR: 'staff_educator',
    STAFF_NETWORK: 'staff_network'
};

/** Logótipo oficial (asset SVG do projeto). */
const LabcodeIconLogo = () => (
    <img
        alt="Labcode"
        className={styles.brandLogoImg}
        decoding="async"
        height="97"
        src={labcodeIconLogoUrl}
        width="97"
    />
);

/**
 * Tela inicial: escolha de acesso → aluno, educadores ou rede escolar.
 */
const LoginHub = () => {
    const [screen, setScreen] = useState(SCREENS.LANDING);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);

    const goLanding = useCallback(() => {
        setScreen(SCREENS.LANDING);
        setError(null);
    }, []);

    const handleStaffSubmit = useCallback(
        async e => {
            e.preventDefault();
            setError(null);
            setPending(true);
            try {
                const data = await staffLogin(email, password);
                const saved = setLabcodeStaffSession(data.token, data.staff);
                if (!saved) {
                    setError(
                        'Não foi possível guardar a sessão neste navegador. Desative modo privado ou bloqueio a cookies/armazenamento.'
                    );
                    return;
                }
                const plat = data.staff && data.staff.role === 'platform_admin';
                const h = window.location.hash || '';
                if (plat) {
                    if (!h.startsWith('#platform')) {
                        window.location.hash = 'platform/dashboard';
                    }
                } else if (!h.startsWith('#staff')) {
                    window.location.hash = 'staff/dashboard';
                }
                setEmail('');
                setPassword('');
            } catch (err) {
                setError(err && err.message ? err.message : 'Erro ao entrar.');
            } finally {
                setPending(false);
            }
        },
        [email, password]
    );

    const staffCopy =
        screen === SCREENS.STAFF_NETWORK
            ? {
                title: 'Rede escolar',
                subtitle: 'Acesso para gestores e Coordenadores'
            }
            : {
                title: 'Educadores',
                subtitle: 'Acesso para professores e equipa pedagógica'
            };

    const pageFooter = (
        <footer className={styles.landingFooter}>
            <span className={styles.footerInline}>
                <a
                    href={URL_PRIVACIDADE}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {'Política de Privacidade'}
                </a>
                <span
                    aria-hidden="true"
                    className={styles.footerSpacer}
                />
                <a
                    href={URL_TERMOS}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {'Termos de Serviço'}
                </a>
            </span>
        </footer>
    );

    return (
        <div
            className={styles.landingShell}
            role="dialog"
            aria-label="Entrar no Labcode"
        >
            {screen === SCREENS.LANDING ? (
                <div className={styles.landingCenter}>
                    <div className={styles.hexLogoWrap}>
                        <LabcodeIconLogo />
                    </div>
                    <main className={styles.choiceCard}>
                        <div className={styles.choiceHead}>
                            <h1 className={styles.choiceTitle}>
                                {'Bem-vindo ao Labcode'}
                            </h1>
                            <p className={styles.choiceSubtitle}>
                                {'Escolha como deseja entrar'}
                            </p>
                        </div>
                        <div className={styles.choiceActions}>
                            <p className={styles.sectionLabel}>
                                {'Na escola'}
                            </p>
                            <div className={styles.choiceStack}>
                                <button
                                    className={styles.primaryButton}
                                    type="button"
                                    onClick={() => {
                                        setError(null);
                                        setScreen(SCREENS.STUDENT);
                                    }}
                                >
                                    {'Sou aluno'}
                                </button>
                                <button
                                    className={styles.primaryButton}
                                    type="button"
                                    onClick={() => {
                                        setError(null);
                                        setScreen(SCREENS.STAFF_EDUCATOR);
                                    }}
                                >
                                    {'Educadores'}
                                </button>
                                <button
                                    className={styles.primaryButton}
                                    type="button"
                                    onClick={() => {
                                        setError(null);
                                        setScreen(SCREENS.STAFF_NETWORK);
                                    }}
                                >
                                    {'Rede escolar'}
                                </button>
                            </div>
                        </div>
                        <div className={styles.contactRow}>
                            <span>{'Primeira vez aqui? '}</span>
                            <a
                                className={styles.linkBlue}
                                href={URL_CONTATO}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {'Entre em contato'}
                            </a>
                        </div>
                    </main>
                </div>
            ) : null}

            {screen !== SCREENS.LANDING ? (
                <div className={styles.subViewCenter}>
                    <div className={styles.hexLogoWrap}>
                        <LabcodeIconLogo />
                    </div>
                    <div className={styles.subViewWrap}>
                        {screen === SCREENS.STUDENT ? (
                            <>
                                <button
                                    className={styles.backLinkStudent}
                                    type="button"
                                    onClick={goLanding}
                                >
                                    {'← Voltar'}
                                </button>
                                <div className={styles.hubInner}>
                                    <StudentLogin
                                        embedded
                                        open
                                        onClose={() => {}}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className={styles.staffCardMock}>
                                <form
                                    noValidate
                                    onSubmit={handleStaffSubmit}
                                >
                                    <button
                                        className={styles.staffBackRow}
                                        type="button"
                                        onClick={goLanding}
                                    >
                                        <span className={styles.staffBackIcon}>
                                            <svg
                                                className={styles.staffBackIconSvg}
                                                fill="none"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                width="16"
                                            >
                                                <path
                                                    d="M10 3 5 8l5 5"
                                                    stroke="#374151"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="1.8"
                                                />
                                            </svg>
                                        </span>
                                        {'Voltar'}
                                    </button>
                                    <h2 className={styles.staffTitleMock}>
                                        {staffCopy.title}
                                    </h2>
                                    <p className={styles.staffSubtitleMock}>
                                        {staffCopy.subtitle}
                                    </p>
                                    {!getLabcodeApiBaseUrl() ? (
                                        <p className={styles.staffHintDev}>
                                            {
                                                'Configure REACT_APP_LABCODE_API_URL no .env e reinicie o npm start.'
                                            }
                                        </p>
                                    ) : null}
                                    {error ? (
                                        <div
                                            aria-live="polite"
                                            className={styles.errorBannerStaff}
                                            role="alert"
                                        >
                                            {error}
                                        </div>
                                    ) : null}
                                    <div className={styles.staffFieldGroup}>
                                        <label
                                            className={styles.staffFieldLabel}
                                            htmlFor="staff-login-email"
                                        >
                                            {'Seu email'}
                                        </label>
                                        <input
                                            autoComplete="username"
                                            className={styles.staffInput}
                                            disabled={pending}
                                            id="staff-login-email"
                                            placeholder="exemplo@gmail.com"
                                            type="email"
                                            value={email}
                                            onChange={e =>
                                                setEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.staffFieldGroup}>
                                        <label
                                            className={styles.staffFieldLabel}
                                            htmlFor="staff-login-password"
                                        >
                                            {'Senha'}
                                        </label>
                                        <input
                                            autoComplete="current-password"
                                            className={styles.staffInput}
                                            disabled={pending}
                                            id="staff-login-password"
                                            placeholder="sua senha"
                                            type="password"
                                            value={password}
                                            onChange={e =>
                                                setPassword(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        className={styles.staffSubmitBtn}
                                        disabled={pending}
                                        type="submit"
                                    >
                                        {pending ? 'Entrando…' : 'Entrar'}
                                    </button>
                                    <div className={styles.forgotRow}>
                                        <a
                                            className={styles.forgotLink}
                                            href={URL_CONTATO}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            {'Esqueci minha senha'}
                                        </a>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {pageFooter}
        </div>
    );
};

export default LoginHub;
